import type { UnitSystem } from './settings';
import { t } from './i18n';

/** Distance for display. Switches unit as the value shrinks, like real nav units. */
export function formatDistance(metres: number, units: UnitSystem): string {
  if (!Number.isFinite(metres) || metres < 0) metres = 0;

  if (units === 'imperial') {
    const feet = metres * 3.28084;
    if (feet < 800) return `${Math.max(0, Math.round(feet / 50) * 50)} ft`;
    const miles = metres / 1609.344;
    return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
  }

  if (metres < 20) return `${Math.round(metres / 5) * 5} m`;
  if (metres < 1000) return `${Math.round(metres / 10) * 10} m`;
  const km = metres / 1000;
  return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
}

/** Short duration: "4 min", "1 hr 12 min" (localised). */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const mins = Math.round(seconds / 60);
  if (mins < 1) return t('dur.lessMin');
  if (mins < 60) return t('dur.min', { n: mins });
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem === 0 ? t('dur.hr', { n: hrs }) : t('dur.hrMin', { h: hrs, m: rem });
}

/** Clock time of arrival. */
/**
 * 24-hour vs 12-hour clock.
 *
 * Held here as module state rather than threaded through every call site, and
 * set once from settings. `toLocaleTimeString` otherwise follows the device
 * locale, which is how a Thai phone set to en-US ends up showing "3:08 PM" to
 * a driver who reads 15:08 on every other instrument in the cab.
 */
let clock24 = true;

export function setClock24(value: boolean): void {
  clock24 = value;
}

export function formatEta(secondsFromNow: number): string {
  const d = new Date(Date.now() + secondsFromNow * 1000);
  return d.toLocaleTimeString(clock24 ? 'en-GB' : undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: !clock24,
  });
}

export function formatSpeed(metresPerSecond: number | null, units: UnitSystem): string {
  if (metresPerSecond == null || !Number.isFinite(metresPerSecond)) return '--';
  const v = units === 'imperial' ? metresPerSecond * 2.236936 : metresPerSecond * 3.6;
  return String(Math.max(0, Math.round(v)));
}

export function speedUnitLabel(units: UnitSystem): string {
  return units === 'imperial' ? 'mph' : 'km/h';
}

export function formatDateTime(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Compact distance for a toast: "600 m", "1.2 km". */
export function formatDistanceShort(metres: number, units: UnitSystem): string {
  return formatDistance(metres, units);
}
